<script setup lang="ts">
import type { MatchTab } from "@/types"
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue"
import {
  DatasetTextIcon,
  TimeIcon,
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import TextMatch from "@/components/FileImport/Match/TextMatch.vue"
import TimeMatch from "@/components/FileImport/Match/TimeMatch.vue"
import StatusDecoration from "@/components/FileImport/StatusDecoration.vue"
import { MatchType } from "@/types"

interface Props {
  /**
   * Tab Items
   */
  tabs: MatchTab[]
  selectedIndex: number
}

interface Emits {
  (event: "change", index: number): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

function handleChange(selectedIndex: number) {
  emit("change", selectedIndex)
}
</script>

<template>
  <TabGroup
    :selected-index="selectedIndex"
    @change="handleChange"
  >
    <TabList class="flex items-center gap-2 overflow-x-auto">
      <div
        v-if="tabs.length > 1"
        class="flex w-fit items-center rounded-full border border-gray-300"
      >
        <template
          v-for="tab in tabs"
          :key="tab.id"
        >
          <Tab v-slot="{ selected }">
            <div
              class="flex cursor-pointer items-center gap-2 rounded-full px-4 py-2.5 text-sm whitespace-nowrap hover:bg-violet-50"
              :class="{
                'bg-violet-500 font-bold text-white hover:text-black': selected,
              }"
            >
              <DatasetTextIcon
                v-if="tab.type === MatchType.Text"
                class="size-4"
              />
              <TimeIcon
                v-if="tab.type === MatchType.Time"
                class="size-4"
              />
              <span>{{ tab.title }} </span>
              <StatusDecoration
                :decorator="tab.decorator()"
                :colour-invert="selected"
              />
            </div>
          </Tab>
        </template>
      </div>
    </TabList>

    <TabPanels class="size-full flex-1 overflow-hidden">
      <template
        v-for="tab in tabs"
        :key="tab.id"
      >
        <TabPanel class="flex h-full flex-col gap-4">
          <TextMatch
            v-if="tab.type === MatchType.Text"
            :key="tab.id"
            :tab="tab"
          />
          <TimeMatch
            v-if="tab.type === MatchType.Time"
            :key="tab.id"
            :tab="tab"
          />
        </TabPanel>
      </template>
    </TabPanels>
  </TabGroup>
</template>
